import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  IconButton,
  Popover,
  Chip,
  Button,
  Stack,
  Box,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import NewsCard from './NewsCard';
import { motion } from 'framer-motion';

function SummaryCard({ data, news, org, onSummaryStatusChange, source, domain }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [status, setStatus] = useState(data?.status || 'pending');

  useEffect(() => {
    if (data?.status) {
      setStatus(data.status);
    }
  }, [data]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    onSummaryStatusChange(newStatus);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const isReltio = source?.toLowerCase() === 'reltio';

  if (!data) return null;

  const getStatusChip = () => {
    switch (status) {
      case 'approved':
        return (
          <Chip
            label="Approved"
            size="small"
            icon={<CheckCircleOutlineIcon sx={{ color: '#08AA66' }} />}
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #08AA66',
              color: '#08AA66',
              fontWeight: 500,
            }}
          />
        );
      case 'rejected':
        return (
          <Chip
            label="Rejected"
            size="small"
            icon={<CancelOutlinedIcon sx={{ color: '#D32F2F' }} />}
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #D32F2F',
              color: '#D32F2F',
              fontWeight: 500,
            }}
          />
        );
      default:
        return (
          <Chip
            label="Status: Pending"
            size="small"
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #FFA500',
              color: '#FFA500',
              fontWeight: 500,
            }}
          />
        );
    }
  };

  return (
    <Card
      component={motion.div}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      sx={{
        borderRadius: 4,
        background: 'rgb(255 255 255 / 16%)',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        height: '100%',
        transition: 'transform 0.3s ease',
        '&:hover': { transform: 'translateY(-4px)' },
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" color="#1C2758" fontWeight={600}>
            🧾 Summary
          </Typography>
          <Tooltip title="Actions" arrow>
            <IconButton onClick={handleClick} size="small">
              <MoreVertIcon sx={{ color: '#1C2758' }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              p: 1,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              minWidth: 250,
            },
          }}
        >
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Box pt="2px">
                <Tooltip title={isReltio ? 'RELTIO' : domain || 'No Domain'}>
                  <Chip
                    label={isReltio ? 'RELTIO' : domain || 'No Domain'}
                    size="small"
                    sx={{
                      backgroundColor: isReltio ? '#1C2758' : '#7c7c7c',
                      color: isReltio ? '#ffffff' : '#ffffff',
                      fontWeight: 300,
                      maxWidth: 125,
                      minWidth: 125,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  />
                </Tooltip>
              </Box>
              <Button
                size="small"
                startIcon={
                  <CheckCircleOutlineIcon
                    sx={{ color: isReltio || status !== 'pending' ? 'grey' : '#08AA66' }}
                  />
                }
                sx={{ color: '#08AA66', fontWeight: 500 }}
                onClick={() => handleStatusChange('approved')}
                disabled={isReltio || status !== 'pending'}
              >
                Approve
              </Button>

              <Button
                size="small"
                startIcon={
                  <CancelOutlinedIcon
                    sx={{ color: isReltio || status !== 'pending' ? 'grey' : '#D32F2F' }}
                  />
                }
                sx={{ color: '#D32F2F', fontWeight: 500 }}
                onClick={() => handleStatusChange('rejected')}
                disabled={isReltio || status !== 'pending'}
              >
                Reject
              </Button>
            </Stack>
          </Stack>
        </Popover>

        <Typography variant="body2" color="#1C2758" m={2}>
          {data.description}
        </Typography>

        <Divider sx={{ my: 2, borderColor: '#0000001f' }} />

        <NewsCard org={org} />
      </CardContent>
    </Card>
  );
}

export default SummaryCard;
